import type { ReactNode } from "react";
import { BluebirdLayout } from "@/components/bluebird/BluebirdLayout";
import { BreadcrumbJsonLd } from "@/components/shared/json-ld";
import { SlimFooter, SubHero, SubMasthead } from "@/components/bluebird/Chrome";
import { Button, H2 } from "@/components/bluebird/primitives";
import { LocationBlock } from "@/components/bluebird/LocationBlock";
import { color, maxWidth, radius, space } from "@/lib/design-tokens";
import { cta } from "@/lib/copy/shop";
import { about } from "@/lib/copy/about";

function About() {
  return (
    <>
      {/* Structured data only — renders no visible markup, so it cannot move a
          pixel in script/design_diff.mjs. Kept from the previous design because
          breadcrumbs are what let a search result show the path to the page. */}
      <BreadcrumbJsonLd items={[{ name: "About", href: "/about" }]} />

      <SubMasthead
        current="/about"
        photo="about-shopfront"
        photoAlt="Ryan standing with cargo bikes outside the Front Range Cargo Bikes shop in Boulder"
        photoWidths={[1200, 1800, 2400]}
      >
        <SubHero
          line1={about.hero.line1}
          line2={about.hero.line2}
          intro={about.hero.intro}
          introMaxWidth="560px"
        />
      </SubMasthead>

      {/* The <main> landmark. A screen-reader user's "skip to content" jump
          lands here, and without it the only landmarks on the page are a banner
          and a footer with everything else unlabelled. <main> is display:block
          with no margin, so it wraps the same boxes at the same positions —
          verified by script/design_diff.mjs, which still reports 0%. */}
      <main>

      <section
        data-section="story"
        style={{
          maxWidth,
          margin: "0 auto",
          padding: `${space.sectionY} ${space.gutter}`,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(min(300px,100%),1fr))",
          gap: "clamp(32px,5vw,72px)",
          alignItems: "center",
        }}
      >
        <div>
          <H2 data-reveal>{about.story.heading}</H2>
          <p style={{ margin: "20px 0 0", fontSize: "16.5px", lineHeight: 1.6, color: color.slate }}>
            {about.story.body1}
          </p>
          <p style={{ margin: "16px 0 0", fontSize: "16.5px", lineHeight: 1.6, color: color.slate }}>
            {about.story.body2}
          </p>
        </div>
        <img
          src={about.story.imageSrc}
          alt={about.story.imageAlt}
          width="640"
          height="480"
          loading="lazy"
          decoding="async"
          data-reveal="image"
          style={{
            width: "100%",
            height: "clamp(260px,32vw,400px)",
            objectFit: "cover",
            objectPosition: "center",
            display: "block",
            borderRadius: radius.card,
          }}
        />
      </section>

      <section
        data-section="who"
        style={{
          maxWidth,
          margin: "0 auto",
          padding: `0 ${space.gutter} ${space.sectionY}`,
        }}
      >
        <H2 data-reveal style={{ margin: "0 0 32px" }}>
          {about.who.heading}
        </H2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(min(260px,100%),1fr))",
            gap: "clamp(18px,2.5vw,28px)",
            maxWidth: "760px",
          }}
        >
          {about.who.people.map((person) => (
            <div key={person.name} data-team-kind={person.kind}>
              <img
                src={person.imageSrc}
                alt={person.imageAlt}
                width="640"
                height="800"
                loading="lazy"
                decoding="async"
                data-reveal="image"
                data-reveal-stagger=""
                style={{
                  width: "100%",
                  aspectRatio: "4/5",
                  objectFit: "cover",
                  objectPosition: "center",
                  display: "block",
                  borderRadius: radius.card,
                }}
              />
              <div data-team-name="" style={{ fontWeight: 700, fontSize: "19px", marginTop: "14px" }}>
                {person.name}
              </div>
              <div style={{ fontSize: "15px", color: color.slate, marginTop: "2px" }}>
                {person.role}
              </div>
            </div>
          ))}
        </div>
      </section>

      <LocationBlock id="visit" dataSection="visit" />

      <section data-section="ride" style={{ background: color.blue, color: color.white }}>
        <div
          style={{
            maxWidth,
            margin: "0 auto",
            padding: `${space.sectionY} ${space.gutter}`,
          }}
        >
          <H2 data-reveal style={{ maxWidth: "720px" }}>
            {about.ride.heading}
          </H2>
          <p
            style={{
              margin: "18px 0 0",
              fontSize: "16.5px",
              lineHeight: 1.6,
              color: color.onBlue,
              maxWidth: "560px",
            }}
          >
            {about.ride.body}
          </p>
          <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", marginTop: "30px" }}>
            <Button
              href={cta.testRideHref}
              variant="red"
              style={{ fontSize: "16px", padding: "15px 26px" }}
            >
              {about.ride.primary}
            </Button>
            <Button
              href={cta.seeBikesHref}
              variant="ghostSoft"
              style={{
                border: "2px solid rgba(255,255,255,.55)",
                fontSize: "16px",
                padding: "13px 26px",
              }}
            >
              {about.ride.secondary}
            </Button>
          </div>
        </div>
      </section>

      </main>

      <SlimFooter borderTop showLocation={false} />
    </>
  );
}

About.layout = (page: ReactNode) => <BluebirdLayout>{page}</BluebirdLayout>;

export default About;
